import { useSlug } from '@/composables/useSlug';
import type { MenuItem } from '@/interfaces/components/menu/itemsMenu.interface';
import {
  DEFAULT_PIPELINE_MENU_ICON,
  PIPELINE_MENU_ICON_BY_REFERENCE_ID,
} from '@/modules/pipeline/constants/Pipeline.constants';
import type { PipelineBoardLayoutMode } from '@/modules/pipeline/types/Pipeline.types';
import type {
  PipelineStageTotals,
  PipelineUrlMap,
  Stage,
} from '@/modules/pipeline/types/Pipeline.types';
import type {
  GetPipelineRecord,
  PipelineSummaryRecord,
  SearchPipelineByIdResponse,
} from '@/modules/pipeline/types/PipelineAPI.types';
import { useCurrencyToNumber } from '@/utils/useCurrencyToNumber';

export const normalizePipelineSlug = (name: string): string => useSlug(name);

export const pipelineStageRequiresDeniedReasons = (
  probability: number | undefined,
): boolean => probability === 0;

export const resolvePipelineBoardLayoutMode = (
  rawLayout: unknown,
): PipelineBoardLayoutMode => {
  const layout =
    rawLayout === undefined || rawLayout === null
      ? undefined
      : Array.isArray(rawLayout)
        ? rawLayout[0]
        : rawLayout;

  return layout === 'table' ? 'table' : 'kanban';
};

export const buildPipelineUrlsToIdsMap = (
  pipelines: Array<PipelineSummaryRecord>,
): PipelineUrlMap => {
  return pipelines.reduce<PipelineUrlMap>((acc, pipeline) => {
    acc[normalizePipelineSlug(pipeline.name)] = pipeline.id;
    return acc;
  }, {});
};

export const buildPipelineIdsToUrlsMap = (
  urlsToIds: PipelineUrlMap,
): PipelineUrlMap => {
  return Object.fromEntries(
    Object.entries(urlsToIds).map(([slug, id]) => [id, slug]),
  );
};

export const toPipelineRecord = (
  pipeline: GetPipelineRecord | SearchPipelineByIdResponse,
): GetPipelineRecord => {
  return {
    id: pipeline.id,
    name: pipeline.name,
    referenceId: pipeline.referenceId,
    stages: pipeline.stages.map((stage) => ({
      id: stage.id,
      pipelineId: stage.pipelineId,
      referenceId: stage.referenceId,
      name: stage.name,
      probability: stage.probability,
      order: stage.order,
      color: stage.color,
      totalItems: stage.totalItems,
      totalAmount: stage.totalAmount,
      sla: stage.sla,
    })),
  };
};

export const resolvePipelineMenuIcon = (
  pipeline: Pick<PipelineSummaryRecord, 'referenceId'>,
): string => {
  const ref = pipeline.referenceId;
  if (ref == null || ref === '') {
    return DEFAULT_PIPELINE_MENU_ICON;
  }
  return PIPELINE_MENU_ICON_BY_REFERENCE_ID[ref] ?? DEFAULT_PIPELINE_MENU_ICON;
};

export const buildPipelineMenuOptions = (
  pipelines: Array<PipelineSummaryRecord>,
): Array<MenuItem> => {
  return pipelines.map((pipeline) => ({
    name: pipeline.name,
    icon: resolvePipelineMenuIcon(pipeline),
    isDefaultIcon: true,
    route: `/meus-negocios/${normalizePipelineSlug(pipeline.name)}`,
    subItems: [],
    isVisible: true,
  }));
};

export const resolvePipelineIdByName = (
  nameOrSlug: string,
  urlsToIds: PipelineUrlMap,
  pipelines: Array<PipelineSummaryRecord>,
): string | null => {
  const normalized = normalizePipelineSlug(nameOrSlug);
  const fromMap = urlsToIds[normalized];
  if (fromMap) return fromMap;

  const fromPipelines = pipelines.find(
    (pipeline) => normalizePipelineSlug(pipeline.name) === normalized,
  );

  return fromPipelines?.id ?? null;
};

export const toOptionalCurrencyRange = (
  min?: string,
  max?: string,
): {
  min?: number;
  max?: number;
} | null => {
  const parsedMin = min ? useCurrencyToNumber(min) : undefined;
  const parsedMax = max ? useCurrencyToNumber(max) : undefined;

  if (parsedMin === undefined && parsedMax === undefined) {
    return null;
  }

  return {
    ...(parsedMin !== undefined && { min: parsedMin }),
    ...(parsedMax !== undefined && { max: parsedMax }),
  };
};

/**
 * Sobrescreve totalItems/totalAmount das etapas com os valores do endpoint de
 * totais. Não reutiliza totais vindos do POST /search — lá o back aplica filtros
 * e os números não batem com o consolidado de totais. Ver com o Alexandre
 * se é possível retornar o totals consolidado no search.
 */
export const mergePipelineStageTotals = (
  stages: Stage[],
  totals: PipelineStageTotals | undefined,
): Stage[] => {
  return stages.map((stage) => {
    const row = totals?.find((item) => item.id === stage.id);

    return {
      ...stage,
      page: stage.page ?? 1,
      loading: stage.loading ?? false,
      totalAmount: row?.totalAmount ?? 0,
      totalItems: row?.totalItems ?? 0,
    };
  });
};
